import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    where,
    Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

export type FeedbackRating = 'terrible' | 'poor' | 'average' | 'good' | 'excellent';
export type FeedbackStatus = 'new' | 'read' | 'resolved';
export type FeedbackCategory = 'bug' | 'feature' | 'general' | 'ui' | 'other';

export interface Feedback {
    id: string;
    userId: string;
    userEmail: string;
    userName: string;
    rating: FeedbackRating | null;
    category: FeedbackCategory | string;
    message: string;
    attachmentUrls: string[];
    pageUrl: string;
    status: FeedbackStatus;
    adminResponse?: string;
    respondedBy?: string;
    respondedAt?: Timestamp;
    createdAt: Timestamp;
}

export interface FeedbackInput {
    rating: FeedbackRating | null;
    category: string;
    message: string;
    attachments: File[];
}

const COLLECTION_NAME = 'feedback';

export const feedbackService = {
    /**
     * Submit new feedback from user
     */
    async submitFeedback(
        userId: string,
        userEmail: string,
        userName: string,
        input: FeedbackInput,
        pageUrl: string
    ): Promise<string> {
        try {
            // Upload attachments to Firebase Storage
            const attachmentUrls: string[] = [];

            for (const file of input.attachments) {
                const timestamp = Date.now();
                const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                const storagePath = `feedback/${userId}/${timestamp}_${sanitizedFileName}`;
                const storageRef = ref(storage, storagePath);

                await uploadBytes(storageRef, file);
                const downloadUrl = await getDownloadURL(storageRef);
                attachmentUrls.push(downloadUrl);
            }

            // Create feedback document
            const feedbackData = {
                userId,
                userEmail,
                userName,
                rating: input.rating,
                category: input.category,
                message: input.message,
                attachmentUrls,
                pageUrl,
                status: 'new' as FeedbackStatus,
                createdAt: Timestamp.now()
            };

            const docRef = await addDoc(collection(db, COLLECTION_NAME), feedbackData);
            console.log('Feedback submitted with ID:', docRef.id);

            return docRef.id;
        } catch (error) {
            console.error('Error submitting feedback:', error);
            throw error;
        }
    },

    /**
     * Get all feedback (for admin panel)
     */
    async getAllFeedback(): Promise<Feedback[]> {
        try {
            const feedbackQuery = query(
                collection(db, COLLECTION_NAME),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(feedbackQuery);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Feedback));
        } catch (error) {
            console.error('Error fetching feedback:', error);
            throw error;
        }
    },

    /**
     * Get feedback by status
     */
    async getFeedbackByStatus(status: FeedbackStatus): Promise<Feedback[]> {
        try {
            const feedbackQuery = query(
                collection(db, COLLECTION_NAME),
                where('status', '==', status),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(feedbackQuery);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Feedback));
        } catch (error) {
            console.error('Error fetching feedback by status:', error);
            throw error;
        }
    },

    /**
     * Get single feedback by ID
     */
    async getFeedbackById(feedbackId: string): Promise<Feedback | null> {
        try {
            const docRef = doc(db, COLLECTION_NAME, feedbackId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return {
                    id: docSnap.id,
                    ...docSnap.data()
                } as Feedback;
            }
            return null;
        } catch (error) {
            console.error('Error fetching feedback:', error);
            throw error;
        }
    },

    /**
     * Update feedback status
     */
    async updateFeedbackStatus(feedbackId: string, status: FeedbackStatus): Promise<void> {
        try {
            const docRef = doc(db, COLLECTION_NAME, feedbackId);
            await updateDoc(docRef, { status });
            console.log('Feedback status updated to:', status);
        } catch (error) {
            console.error('Error updating feedback status:', error);
            throw error;
        }
    },

    /**
     * Add admin response to feedback
     */
    async respondToFeedback(
        feedbackId: string,
        response: string,
        adminEmail: string
    ): Promise<void> {
        try {
            const docRef = doc(db, COLLECTION_NAME, feedbackId);
            await updateDoc(docRef, {
                adminResponse: response,
                respondedBy: adminEmail,
                respondedAt: Timestamp.now(),
                status: 'resolved' as FeedbackStatus
            });
            console.log('Response added to feedback:', feedbackId);
        } catch (error) {
            console.error('Error responding to feedback:', error);
            throw error;
        }
    },

    /**
     * Delete feedback entry
     */
    async deleteFeedback(feedbackId: string): Promise<void> {
        try {
            const docRef = doc(db, COLLECTION_NAME, feedbackId);
            await deleteDoc(docRef);
            console.log('Feedback deleted:', feedbackId);
        } catch (error) {
            console.error('Error deleting feedback:', error);
            throw error;
        }
    },

    /**
     * Get feedback count by status (for admin dashboard)
     */
    async getFeedbackCounts(): Promise<{ new: number; read: number; resolved: number; total: number }> {
        try {
            const snapshot = await getDocs(collection(db, COLLECTION_NAME));
            const counts = { new: 0, read: 0, resolved: 0, total: 0 };

            snapshot.docs.forEach(doc => {
                const status = doc.data().status as FeedbackStatus;
                counts.total++;
                if (status === 'new') counts.new++;
                else if (status === 'read') counts.read++;
                else if (status === 'resolved') counts.resolved++;
            });

            return counts;
        } catch (error) {
            console.error('Error fetching feedback counts:', error);
            throw error;
        }
    }
};

export default feedbackService;
